import type {
  DocumentCategory,
  DocumentFileSource,
  DocumentTag,
} from "@/constants/Documents";
import api from "@/store/api";

interface GetUploadUrlRequest {
  fileName: string;
  linkedNoteId: string;
}

interface GetUploadUrlResponse {
  attachmentExternalId: string;
  attachmentUploadUrl: string;
}

interface GetUploadUrlV2Request {
  chatChannelId?: string;
  documentCategory?: DocumentCategory;
  documentTags?: DocumentTag[];
  fileName: string;
  fileSize: string;
  fileSource?: DocumentFileSource;
  isSensitive?: boolean;
  patientExternalId: string;
}

interface GetUploadUrlV2Response {
  data?: {
    documentKey: string;
    uploadUrl: string;
  };
  httpStatus: number;
  status: string;
}

interface UploadFileRequest {
  file: File;
  uploadUrl: string;
}

interface VoidPatientDocumentRequest {
  documentExternalId: string;
  patientExternalId: string;
  voidReasonCode: string;
}

const documentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUploadInsuranceCardUrl: build.query<
      GetUploadUrlResponse,
      GetUploadUrlRequest
    >({
      query: ({ fileName, linkedNoteId }) => ({
        method: "GET",
        url: `/clinical-data/family/notes/${linkedNoteId}/attachments/${fileName}/upload-url`,
      }),
    }),
    getUploadInsuranceCardUrlV2: build.query<
      GetUploadUrlV2Response,
      GetUploadUrlV2Request
    >({
      query: ({
        chatChannelId: channelId,
        documentCategory: type,
        documentTags: tags,
        fileName,
        fileSize,
        fileSource,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- API field doesn't follow repo's naming convention
        isSensitive: sensitive,
        patientExternalId,
      }) => ({
        body: {
          channelId,
          fileName,
          fileSize,
          fileSource,
          patientExternalId,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- API field doesn't follow repo's naming convention
          sensitive,
          tags,
          type,
        },
        method: "POST",
        url: "/clinical-data/family/v2/patient-documents",
      }),
    }),
    uploadFile: build.mutation<string, UploadFileRequest>({
      queryFn: async ({ file, uploadUrl }) => {
        const response = await fetch(uploadUrl, {
          body: file,
          method: "PUT",
        });
        if (response.ok) {
          return { data: "success" };
        }
        const errorText = await response.text();
        return {
          error: { data: errorText, status: response.status },
        };
      },
    }),
    voidPatientDocument: build.mutation<void, VoidPatientDocumentRequest>({
      query: ({ documentExternalId, patientExternalId, voidReasonCode }) => ({
        method: "DELETE",
        params: {
          patientExternalId,
          voidReasonCode,
        },
        url: `/clinical-data/family/v2/patient-documents/${documentExternalId}`,
      }),
    }),
  }),
});

const {
  useLazyGetUploadInsuranceCardUrlQuery,
  useLazyGetUploadInsuranceCardUrlV2Query,
  useUploadFileMutation,
  useVoidPatientDocumentMutation,
} = documentsApi;

export {
  useLazyGetUploadInsuranceCardUrlQuery,
  useLazyGetUploadInsuranceCardUrlV2Query,
  useUploadFileMutation,
  useVoidPatientDocumentMutation,
};
export type { GetUploadUrlV2Request };
